// @ts-strict

/* eslint-disable no-unused-vars */
import { APP_BASE_HREF, CommonModule, Location, NgOptimizedImage } from '@angular/common';
import { ApplicationRef, CUSTOM_ELEMENTS_SCHEMA, inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration
} from '@angular/common/http';
import { AppComponent, appComponentProviders } from './app.component';
import { appRouting, appRoutingProviders } from './app.routing';
import { DeactivateGuard } from './deactivate-guard';
import { ActivateGuard } from './activate-guard';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpSSOService, LMModalOpenService, LMModalWrapperService, LMRouteComponentService } from './services';
import {
  CapitalizePipe,
  KeysPipe,
  LMAsOfDate,
  LMBooleanAsSwitchTooltipPipe,
  LMBooleanAsTextPipe,
  LMEscapePipe,
  LMFieldOperatorPipe,
  LMFormatLabelPipe,
  LMFormatMessagePipe,
  LMModelFieldIsBlankPipe,
  LMModelFieldToDisplayPipe,
  LMRichTextPipe,
  LMStateAsTextPipe
} from './pipes';

import { SohoComponentsModule } from 'ids-enterprise-ng';

import { LMRouteReuseStrategy } from './lm-route-resuse-strategy';

import {
  AccessDeniedErrorComponent,
  LMActionFormDialogComponent,
  LMActionPickerComponent,
  LMAddSortDialogComponent,
  LMAddTagDialogComponent,
  LMAddToMetricComputeDialogComponent,
  LMApplicationMenuAsOfDateDialogComponent,
  LMApplicationMenuProxyMenuComponent,
  LMApplicationMenuSearchComponent,
  LMApplicationMenuSearchFieldComponent,
  LMApplicationMenuSettingsDialogComponent,
  LMApplicationMenuSubmenuComponent,
  LMApplicationMenuUserContextDialogComponent,
  LMApplicationMenuUserContextFieldComponent,
  LMApplicationMenuV3Component,
  LMAuditLogCompareDialogComponent,
  LMAuditLogComponent,
  LMBlankLineComponent,
  LMBlockItemComponent,
  LMBuilderColumnTotalsComponent,
  LMBuilderComponent,
  LMBuilderDataSourceComponent,
  LMBuilderDisplayComponent,
  LMBuilderDisplayFieldComponent,
  LMBuilderDisplayFieldsComponent,
  LMBuilderDisplayFieldSortingComponent,
  LMBuilderExportComponent,
  LMBuilderFilterComponent,
  LMBuilderDisplayFieldAlertsComponent,
  LMBuilderPaletteCollapsibleSectionComponent,
  LMBuilderPaletteComponent,
  LMBuilderPreviewComponent,
  LMBuilderPropertiesComponent,
  LMBuilderPropertyPanelComponent,
  LMBuilderReportCategorizationComponent,
  LMBuilderSharingComponent,
  LMBuilderSummaryTotalsComponent,
  LMBuilderTotalingComponent,
  LMBuilderTotalByComponent,
  LMBusclassFieldComponent,
  LMButtonComponent,
  LMCaptchaControlComponent,
  LMChartBarComponent,
  LMChartColumnComponent,
  LMChartLineComponent,
  LMChartPieComponent,
  LMChartRadarComponent,
  LMCheckboxComponent,
  LMCheckboxGroupComponent,
  LMCheckcontrolComponent,
  LMCodeBlockComponent,
  LMCodeBlockEditorComponent,
  LMColumnsDistributedComponent,
  LMColumnsGridComponent,
  LMColumnsImageFirstColumnComponent,
  LMColumnsPercentComponent,
  LMColumnsTableComponent,
  LMColumnsUndistributedComponent,
  LMCompactFormDialogComponent,
  LMComponentInfoComponent,
  LMCompoundFieldComponent,
  LMComputeBuilderComponent,
  LMComputeBuilderRowComponent,
  LMCreateReportDialogComponent,
  LMCreateReportFieldsDialogComponent,
  LMCreateReportSearchListDialogComponent,
  LMDashboardPanelCardComponent,
  LMDashboardPanelFormComponent,
  LMDashboardPanelFormTemplateComponent,
  LMDashboardPanelListComponent,
  LMDashboardPanelUrlComponent,
  LMDashboardViewComponent,
  LMDateComponent,
  LMDefaultRouteComponent,
  LMDerivedComputeBuilderComponent,
  LMDetailSectionCardComponent,
  LMDetailSectionDashBoardComponent,
  LMDetailSectionFormComponent,
  LMDetailSectionListComponent,
  LMDetailSectionMultiListComponent,
  LMDetailSectionNotFoundComponent,
  LMDetailSectionsComponent,
  LMDetailSectionUrlComponent,
  LMDirtyCheckDialogComponent,
  LMDropdownComponent,
  LMDropdownSohoComponent,
  LMEditableFieldComponent,
  LMElementLayoutComponent,
  LMFacadeBusinessClassDetailComponent,
  LMFacadeBusinessClassPanelComponent,
  LMFacadeBusinessObjectComponent,
  LMFacadeExplorerComponent,
  LMFacadeModuleComponent,
  LMFacadeModuleHeaderComponent,
  LMFacadeModulesComponent,
  LMFacadePickerDialogComponent,
  LMFacadeReportTemplateDetailComponent,
  LMFacadeReportTemplatePanelComponent,
  LMFacadeSearchResultsComponent,
  LMFacadeTileComponent,
  LMFacadeToggleButtonComponent,
  LMFacadeUnknownDetailComponent,
  LMFieldLayoutComponent,
  LMFieldOptionsComponent,
  LMFieldPickerComponent,
  LMFieldPickerFieldOperatorComponent,
  LMFieldPickerHelpComponent,
  LMFieldPickerLookupComponent,
  LMFieldPickerPeriodOperatorComponent,
  LMFieldStyleDirective,
  LMFieldUpperCaseDirective,
  LMFileuploadComponent,
  LMFileuploadSohoComponent,
  LMFilterBuilderComponent,
  LMFilterBuilderFieldLookupComponent,
  LMFilterBuilderRowComponent,
  LMFormComponent,
  LMFormContentComponent,
  LMFormContentCompositeComponent,
  LMFormContentFormComponent,
  LMFormContentInstanceCountComponent,
  LMFormContentManualRepresentationComponent,
  LMFormContentMatrixComponent,
  LMFormContentMenuComponent,
  LMFormContentSearchComponent,
  LMFormContentStepprocessComponent,
  LMFormContentTemplateComponent,
  LMFormLayoutComponent,
  LMFormPanelActionIsComponent,
  LMFormPanelDashboardComponent,
  LMFormPanelFormComponent,
  LMFormPanelIndependentFormComponent,
  LMFormPanelListComponent,
  LMFormPanelMultiListComponent,
  LMFormPanelNotFoundComponent,
  LMFormPanelTemplateComponent,
  LMFormPanelTextComponent,
  LMFormPanelUrlComponent,
  LMGenAIPromptDialogComponent,
  LMHeaderComponent,
  LMHelperListDialogComponent,
  LMHomePageComponent,
  LMHomeRouteComponent,
  LMHorizontalScrollerComponent,
  LMHtmlPageComponent,
  LMHtmlUrlDocComponent,
  LMHyperlinkComponent,
  LMImageComponent,
  LMKeyboardShortcutsDialogComponent,
  LMLabelModule,
  LMLargeImageIsDialogComponent,
  LMLayoutUtilDirective,
  LMListCalendarComponent,
  LMListCardComponent,
  LMListChartComponent,
  LMListChartSelectorsComponent,
  LMListComponent,
  LMListContentComponent,
  LMListContentFilterListComponent,
  LMListCubeViewerComponent,
  LMListDatagridSohoComponent,
  LMListDatagridWrapperComponent,
  LMListGalleryComponent,
  LMListGridComponent,
  LMListHierarchyComponent,
  LMListImageComponent,
  LMListPagerWrapperComponent,
  LMListPersonalizationDisplayComponent,
  LMListPersonalizationRestrictedActionsComponent,
  LMListPersonalizationSourceComponent,
  LMListPersonalizationToolbarComponent,
  LMListSearchFormDialogComponent,
  LMListSearchTagsComponent,
  LMListSelectionTagsComponent,
  LMListTreeComponent,
  LMListViewComponent,
  LMLoginComponent,
  LMLookupComponent,
  LMLookupDialogComponent,
  LMManualComponent,
  LMManualRepresentationComponent,
  LMMenuFieldComponent,
  LMMouseOverTextComponent,
  LMNavigationComponent,
  LMPageComponent,
  LMPagePaneDashBoardComponent,
  LMPagePanelComponent,
  LMPagePaneListComponent,
  LMPagePaneMenuComponent,
  LMPagePaneMultiListComponent,
  LMPagePaneUrlComponent,
  LMParagraphLayoutComponent,
  LMPendingChangesDialogComponent,
  LMPreviewOptionsDialogComponent,
  LMPrintCsvDialogComponent,
  LMPrintPdfDialogComponent,
  LMProcessIndicatorComponent,
  LMProgressComponent,
  LMRadiobuttonComponent,
  LMRadiobuttonSohoComponent,
  LMRatingComponent,
  LMReportFieldPickerButtonComponent,
  LMReportFieldPickerDrawerComponent,
  LMReportFieldPickerViewerComponent,
  LMReportPersonalizationDialogComponent,
  LMReportsCatalogComponent,
  LMReportsListComponent,
  LMReportsSearchComponent,
  LMReportsSecurityComponent,
  LMReportsSecurityListComponent,
  LMResponseRequiredModalFormComponent,
  LMRichtextComponent,
  LMSavedSearchManageComponent,
  LMScanOptionsComponent,
  LMSectionedCardComponent,
  LMSelectDialogComponent,
  LMSelectRecordDialogComponent,
  LMSettingsComponent,
  LMSimpleCardComponent,
  LMSimpleProgressComponent,
  LMSubFormDialogComponent,
  LMSuiteHomeComponent,
  LMSwatchDirective,
  LMTemplateFormRecordComponent,
  LMTextareaComponent,
  LMTextComponent,
  LMTextSohoComponent,
  LMTextStyleDirective,
  LMTimeComponent,
  LMToolbarComponent,
  LMTrackDirtyDirective,
  LMTwelveColumnGridComponent,
  LMTwelveColumnPanelCardComponent,
  LMTwelveColumnPanelFormComponent,
  LMTwelveColumnPanelFormTemplateComponent,
  LMTwelveColumnPanelListComponent,
  LMTwelveColumnPanelUrlComponent,
  LMViewExceptionDetailsComponent,
  LMViewMoreComponent,
  LMVisibleWhenDirective,
  LMWidgetTitleComponent,
  LMColumnarAdhocReportComponent,
  LMColumnarAdhocReportDimensionsComponent,
  LMColumnarAdhocReportMeasuresComponent,
  LMAdHocSearchComponent,
} from './components';

import { SafePipe } from '@pipes/safeUrl.pipe';
import { LMListSearchFormDirective } from '@list/lm-list-search-form.directive';
import { LMListLookupDialogComponent } from '@list/lm-list-lookup-dialog.component';
import { LMContextService } from '@services/lm-context.service';
import { HttpCSRFInterceptorService } from '@services/http.csrf.interceptor.service';
import { LMListAuditComparePanelComponent } from '@list/lm-list-audit-compare-panel.component';
import { LMActionComponent } from '@action/lm-action.component';
import { LMLocaleInitializerModule } from '@locale/lm-locale-initializer.module';
import { DrawerModule } from '@drawer/drawer.module';
import { TransactionModule } from '@transaction/transaction.module';
import { CubesModule } from '@modal/cubes/cubes.module';
import { LMNotificationComponent } from '@notification/lm-notification.component';
import { UserWebRequestHttpInterceptor } from './http.intercepter';
import { LMHttpIntercepterService } from '@services/lm-http-intercepter.service';
import { LMLplEditorComponent } from '@lplEditor/lm-lpl-editor.component';
import { LMFieldEditorCompareComponent, LMFieldEditorComponent } from '@field/editor';
import { LMListChartAggregationComponent } from '@list/lm-list-chart-aggregation';
import { LMCubeCellRelTestComponent } from '@modal/cube-cell-rel';
import { LMLplEditorCompareComponent } from '@lplEditor/lm-lpl-editor-compare.component';
import { LMFileUploadProgressComponent } from '@services/file-upload/lm-file-upload-progress.component';
import { LMProcessIndicatorTooltipPipe } from '@field/processindicator/lm-process-indicator-tooltip.pipe';
import { LMCommonModule } from '@app/lm-common.module';
import { LMNewThemeHoverDirective } from '../directives/lm-new-theme-hover.directive';
import { LMPollAndPrintDialogComponent } from '@modal/print-dialog/lm-poll-and-print-dialog.component';
import { LMListDropdownComponent } from '@components/lmlistdropdown';
import { LMBlockGridComponent } from '@components/blockgrid';
import { LMRepositoryHelperComponent } from '@lplEditor/lm-repository-helper.component';
import { NgYggdrasilModule, TRACKING_CLIENT } from '@yggdrasil/angular';
import { DevTrackingClient } from '@yggdrasil/core';
import { LMAddTotalDialogComponent } from '@modal/lm-add-total-dialog.component';
import { LMBannerHeaderComponent } from './components/banner/lm-banner-header.component';
import { LMGainsightInitializerModule } from '@insights/lm-gainsight-initializer.module';
import { LMMenuSecurityComponent, LMMenuSecurityInfoComponent, } from '@components/menusecurity';
import {
  LMDrillAroundComponent,
  LMDrillNavigationComponent,
  LMDrillPointViewComponent,
  LMDrillViewPdfComponent,
  LMDrillViewUnknownComponent
} from '@components/drillaround';
import { LMDrillPointListComponent } from './components/drillaround/lm-drill-point-list.component';
import { LMGenAISummarizeDialogComponent } from '@modal/lm-genai-summarize-dialog.component';
import { LMFieldPickerOperatorComponent } from '@modal/field-picker/lm-field-picker-operator.component';
import { LMWebAppInitializerModule } from '@services/webapp/lm-webapp-initializer.module';
import { LMTooltipDirective } from '../directives/lm-tooltip.directive';
import { LMDragAndDropDirective } from '../directives/lm-drag-and-drop.directive';
import { LMKeyboardEventBlockerDirective } from '../directives/lm-keyboard-event-blocker.directive';
import { LMLiveRegionDirective } from "../directives/lm-live-region.directive";
import { LMSidebarResizeComponent } from '@app/sidebar';
import { DragDropModule } from "@angular/cdk/drag-drop";
import { LMSecurityUtilService } from './utils/lm-security-util.service';
import { LMXLFileUploadProgressComponent } from '@services/file-upload/lm-xlfile-upload-progress.component';

@NgModule({
  declarations: [
    AppComponent,
    // Application Menu (previously lm-banner)
    LMApplicationMenuAsOfDateDialogComponent,
    LMApplicationMenuProxyMenuComponent,
    LMApplicationMenuSearchComponent,
    LMApplicationMenuSearchFieldComponent,
    LMApplicationMenuSettingsDialogComponent,
    LMApplicationMenuSubmenuComponent,
    LMApplicationMenuUserContextDialogComponent,
    LMApplicationMenuUserContextFieldComponent,
    LMApplicationMenuV3Component,
    LMSettingsComponent,
    LMReportsCatalogComponent,
    LMReportsSecurityComponent,
    LMReportsListComponent,
    LMReportsSecurityListComponent,
    LMReportsSearchComponent,
    LMHtmlPageComponent,
    LMHtmlUrlDocComponent,
    LMHomePageComponent,
    LMSimpleCardComponent,
    LMSuiteHomeComponent,
    AccessDeniedErrorComponent,
    LMVisibleWhenDirective,
    // form stuff
    LMColumnsDistributedComponent,
    LMColumnsGridComponent,
    LMColumnsUndistributedComponent,
    LMColumnsPercentComponent,
    LMColumnsTableComponent,
    LMColumnsImageFirstColumnComponent,
    LMFormContentCompositeComponent,
    LMFormPanelActionIsComponent,
    LMFormPanelDashboardComponent,
    LMFormPanelFormComponent,
    LMFormPanelUrlComponent,
    LMFormPanelIndependentFormComponent,
    LMFormContentMatrixComponent,
    LMComponentInfoComponent,
    LMFormPanelListComponent,
    LMFormPanelMultiListComponent,
    LMFormContentTemplateComponent,
    LMFormPanelNotFoundComponent,
    LMFormContentInstanceCountComponent,
    LMFormContentManualRepresentationComponent,
    LMElementLayoutComponent,
    LMFieldLayoutComponent,
    LMFieldPickerLookupComponent,
    LMFormComponent,
    LMFormContentComponent,
    LMFormContentSearchComponent,
    LMFormContentFormComponent,
    LMFormLayoutComponent,
    LMLayoutUtilDirective,
    LMFormPanelTemplateComponent,
    LMTemplateFormRecordComponent,
    LMFormPanelTextComponent,
    LMHeaderComponent,
    LMParagraphLayoutComponent,
    LMFormContentStepprocessComponent,
    LMManualComponent,
    KeysPipe,
    // list stuff
    LMChartColumnComponent,
    LMChartBarComponent,
    LMChartLineComponent,
    LMChartPieComponent,
    LMChartRadarComponent,
    LMListChartAggregationComponent,
    LMCodeBlockComponent,
    LMCodeBlockEditorComponent,
    LMListChartComponent,
    LMListChartSelectorsComponent,
    LMListCalendarComponent,
    LMListComponent,
    LMListViewComponent,
    LMListContentComponent,
    LMListCubeViewerComponent,
    LMListGridComponent,
    LMListDatagridWrapperComponent,
    LMListDatagridSohoComponent,
    LMListImageComponent,
    LMListPagerWrapperComponent,
    LMBlockItemComponent,
    LMDetailSectionsComponent,
    LMDetailSectionCardComponent,
    LMDetailSectionDashBoardComponent,
    LMDetailSectionFormComponent,
    LMDetailSectionListComponent,
    LMDetailSectionMultiListComponent,
    LMDetailSectionUrlComponent,
    LMDetailSectionNotFoundComponent,
    LMListSearchTagsComponent,
    LMListSelectionTagsComponent,
    LMListTreeComponent,
    LMListHierarchyComponent,
    LMListLookupDialogComponent,
    LMListSearchFormDirective,
    LMListSearchFormDialogComponent,
    LMListContentFilterListComponent,
    LMListCardComponent,
    // page stuff
    LMPageComponent,
    LMPagePaneListComponent,
    LMPagePanelComponent,
    LMPagePaneDashBoardComponent,
    LMPagePaneMenuComponent,
    LMPagePaneMultiListComponent,
    LMPagePaneUrlComponent,
    // field stuff
    LMBusclassFieldComponent,
    LMAddToMetricComputeDialogComponent,
    LMButtonComponent,
    LMBlankLineComponent,
    LMCaptchaControlComponent,
    LMCheckboxComponent,
    LMCheckboxGroupComponent,
    LMCheckcontrolComponent,
    LMCompoundFieldComponent,
    LMFileuploadComponent,
    LMFileuploadSohoComponent,
    LMDateComponent,
    LMDropdownComponent,
    LMDropdownSohoComponent,
    LMFieldEditorComponent,
    LMFieldEditorCompareComponent,
    LMFieldOptionsComponent,
    LMScanOptionsComponent,
    LMFieldStyleDirective,
    LMFieldUpperCaseDirective,
    LMImageComponent,
    LMLookupComponent,
    LMLookupDialogComponent,
    LMManualRepresentationComponent,
    LMMenuFieldComponent,
    LMMouseOverTextComponent,
    LMRadiobuttonComponent,
    LMRadiobuttonSohoComponent,
    LMRatingComponent,
    LMRichtextComponent,
    LMTextComponent,
    LMTextSohoComponent,
    LMTextareaComponent,
    LMTextStyleDirective,
    LMTrackDirtyDirective,
    LMTimeComponent,
    LMProgressComponent,
    LMProcessIndicatorComponent,
    LMHyperlinkComponent,
    LMSimpleProgressComponent,
    LMViewExceptionDetailsComponent,
    // toolbar stuff
    LMToolbarComponent,
    // banner stuff
    LMBannerHeaderComponent,
    LMSwatchDirective,
    // LMLegacySubMenuComponent,
    // routes components
    LMDefaultRouteComponent,
    LMHomeRouteComponent,
    LMNavigationComponent,
    LMActionComponent,
    // modal dialog
    LMActionFormDialogComponent,
    LMActionPickerComponent,
    LMCompactFormDialogComponent,
    // LMComputeEditorDialogComponent,
    // LMFieldEditorDialogComponent,
    LMHelperListDialogComponent,
    LMResponseRequiredModalFormComponent,
    LMLoginComponent,
    LMDirtyCheckDialogComponent,
    LMGenAIPromptDialogComponent,
    LMSubFormDialogComponent,
    LMGenAISummarizeDialogComponent,
    LMSelectRecordDialogComponent,
    LMSelectDialogComponent,
    LMPendingChangesDialogComponent,
    LMPrintCsvDialogComponent,
    LMPrintPdfDialogComponent,
    LMPollAndPrintDialogComponent,
    LMKeyboardShortcutsDialogComponent,
    LMLargeImageIsDialogComponent,
    // create report
    LMReportPersonalizationDialogComponent,
    LMListPersonalizationSourceComponent,
    LMListPersonalizationToolbarComponent,
    LMListPersonalizationRestrictedActionsComponent,
    LMListPersonalizationDisplayComponent,
    LMCreateReportDialogComponent,
    LMCreateReportFieldsDialogComponent,
    LMCreateReportSearchListDialogComponent,
    LMReportFieldPickerButtonComponent,
    LMReportFieldPickerViewerComponent,
    LMReportFieldPickerDrawerComponent,
    LMEditableFieldComponent,
    // pipes
    CapitalizePipe,
    SafePipe,
    LMAsOfDate, // menu
    LMFormContentMenuComponent,
    LMMenuSecurityComponent,
    LMMenuSecurityInfoComponent,
    // dashboards
    LMDashboardPanelCardComponent,
    LMDashboardPanelFormComponent,
    LMDashboardPanelFormTemplateComponent,
    LMDashboardPanelListComponent,
    LMDashboardPanelUrlComponent,
    LMDashboardViewComponent,
    LMWidgetTitleComponent,
    // twelve column grid
    LMTwelveColumnGridComponent,
    LMTwelveColumnPanelCardComponent,
    LMTwelveColumnPanelFormComponent,
    LMTwelveColumnPanelFormTemplateComponent,
    LMTwelveColumnPanelListComponent,
    LMTwelveColumnPanelUrlComponent,
    // custom
    LMViewMoreComponent,
    LMLplEditorComponent,
    LMLplEditorCompareComponent,
    LMListDropdownComponent,
    // audit log
    LMAuditLogComponent,
    LMAuditLogCompareDialogComponent,
    LMListAuditComparePanelComponent,
    // pipes
    LMBooleanAsTextPipe,
    LMBooleanAsSwitchTooltipPipe,
    LMFormatLabelPipe,
    LMFormatMessagePipe,
    LMModelFieldToDisplayPipe,
    LMModelFieldIsBlankPipe,
    LMRichTextPipe,
    LMStateAsTextPipe,
    LMEscapePipe,
    LMFieldOperatorPipe,
    LMProcessIndicatorTooltipPipe,
    LMFilterBuilderComponent,
    LMFilterBuilderFieldLookupComponent,
    LMFieldPickerComponent,
    LMFieldPickerHelpComponent,
    LMFieldPickerOperatorComponent,
    LMFieldPickerFieldOperatorComponent,
    LMFieldPickerPeriodOperatorComponent,
    LMFilterBuilderRowComponent,
    LMPreviewOptionsDialogComponent,
    LMCodeBlockComponent,
    LMCodeBlockEditorComponent,
    LMNotificationComponent,
    // Drill Around
    LMDrillAroundComponent,
    LMDrillNavigationComponent,
    LMDrillPointListComponent,
    LMDrillPointViewComponent,
    LMDrillViewPdfComponent,
    LMDrillViewUnknownComponent,
    // Cube cell relation
    LMCubeCellRelTestComponent,
    // Compute Field UI
    LMComputeBuilderComponent,
    LMComputeBuilderRowComponent,
    LMFileUploadProgressComponent,
    LMXLFileUploadProgressComponent,
    LMDerivedComputeBuilderComponent,
    LMNewThemeHoverDirective,
    LMRepositoryHelperComponent,
    LMSavedSearchManageComponent,
    LMHorizontalScrollerComponent,
    LMListGalleryComponent,
    LMSectionedCardComponent,
    LMBlockGridComponent,
    LMAddSortDialogComponent,
    LMAddTagDialogComponent,
    LMAddTotalDialogComponent,
    // wrappers
    LMTooltipDirective,
    LMLiveRegionDirective,
    LMDragAndDropDirective,
    LMKeyboardEventBlockerDirective,
    // resizable sidebar stuff
    LMSidebarResizeComponent,
    //Adhoc Search
    LMAdHocSearchComponent,
    // Facade
    LMFacadeUnknownDetailComponent,
    LMFacadeReportTemplateDetailComponent,
    LMFacadeBusinessClassDetailComponent,
    LMFacadeReportTemplatePanelComponent,
    LMFacadeBusinessClassPanelComponent,
    LMFacadeExplorerComponent,
    LMFacadeModulesComponent,
    LMFacadeModuleHeaderComponent,
    LMFacadeSearchResultsComponent,
    LMFacadeTileComponent,
    LMFacadeToggleButtonComponent,
    LMFacadeBusinessObjectComponent,
    LMFacadePickerDialogComponent,
    LMFacadeModuleComponent,
    // Builder
    LMBuilderComponent,
    LMBuilderDisplayFieldsComponent,
    LMBuilderDisplayFieldComponent,
    LMBuilderPropertyPanelComponent,
    LMBuilderPaletteComponent,
    LMBuilderPaletteCollapsibleSectionComponent,
    LMBuilderPropertiesComponent,
    LMBuilderReportCategorizationComponent,
    LMBuilderDataSourceComponent,
    LMBuilderDisplayComponent,
    LMBuilderExportComponent,
    LMBuilderSharingComponent,
    LMBuilderDisplayFieldSortingComponent,
    LMBuilderTotalingComponent,
    LMBuilderColumnTotalsComponent,
    LMBuilderSummaryTotalsComponent,
    LMBuilderPreviewComponent,
    LMBuilderFilterComponent,
    LMBuilderDisplayFieldAlertsComponent,
    LMBuilderTotalByComponent,
    LMColumnarAdhocReportComponent,
    LMColumnarAdhocReportDimensionsComponent,
    LMColumnarAdhocReportMeasuresComponent,
  ],
  bootstrap: [ AppComponent ],
  exports: [],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ],
  imports: [
    BrowserModule,
    LMLocaleInitializerModule,
    LMWebAppInitializerModule,
    LMGainsightInitializerModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    BrowserAnimationsModule,
    appRouting,
    LMLabelModule,
    // soho imports down here
    SohoComponentsModule,
    DrawerModule,
    TransactionModule,
    LMCommonModule,
    CubesModule,
    NgYggdrasilModule,
    NgOptimizedImage,
    DragDropModule,
  ],
  providers: [
    LMSecurityUtilService,
    LMContextService,
    LMModalWrapperService,
    LMModalOpenService,
    provideAppInitializer(() => {
      const contextService = inject(LMContextService);
      return contextService.setContext(LandmarkContext);
    }),
    appRoutingProviders,
    appComponentProviders,
    { provide: ActivateGuard, useClass: ActivateGuard },
    { provide: DeactivateGuard, useClass: DeactivateGuard },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpCSRFInterceptorService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpSSOService,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: UserWebRequestHttpInterceptor,
      multi: true
    },
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: RouteReuseStrategy,
      useClass: LMRouteReuseStrategy
    },
    LMRouteComponentService,
    LMHttpIntercepterService,
    { provide: TRACKING_CLIENT, useValue: new DevTrackingClient() },
    provideHttpClient(withInterceptorsFromDi(), withXsrfConfiguration({
      cookieName: 'SSO.CSRF',
      headerName: 'SSO.CSRF',
    })),
  ] })

export class AppModule {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(private location: Location, applicationRef: ApplicationRef) {
    // if (!environment.production) {
    //
    //   const originalTick = applicationRef.tick;
    //   applicationRef.tick = function() {
    //     const  before = window.performance.now();
    //     const retValue = originalTick.apply(this, arguments);
    //     const after = window.performance.now();
    //     const runTime = after - before;
    //
    //     console.log(`CHANGE DETECTION TIME: ${runTime}`);
    //     // lmLoggerService.log(`CHANGE DETECTION TIME: ${runTime}`);
    //
    //     return retValue;
    //   };
    // }
    // console.log('START: Infor Landmark Xi');
    // tslint:disable-next-line:no-console
    // console.time('Infor Landmark Xi');

    // Change Detection timing
    // Run ng.profiler.timeChangeDetection() in the browser console

    // Allow () in the URL
    const path: string = location.path(true); // call with true to include any hash
    location.replaceState(path.replace(/\(/g, '%28').replace(/\)/g, '%29'));
  }
}
